import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Check, CircleAlert, Sprout, Target, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api, ApiError } from "../../api/client";
import { appraisalName, daysUntil, formatDate } from "../../utils/appraisal";

/** Where an agreed objective stands. null = no update yet. */
export type Progress = "on_track" | "need_help" | "done" | null;

export interface AgreedObjective {
  id: number;
  objective: string;
  how_measured?: string | null;
  due_date: string | null;
  progress: Progress;
  progress_note?: string | null;
  progress_updated_at?: string | null;
}

export const PROGRESS_OPTIONS: { value: Exclude<Progress, null>; label: string; tone: string; active: string }[] = [
  { value: "on_track", label: "On track", tone: "text-blue-700", active: "border-blue-600 bg-blue-600 text-white" },
  { value: "need_help", label: "Need help", tone: "text-amber-800", active: "border-amber-500 bg-amber-500 text-white" },
  { value: "done", label: "Done", tone: "text-green-700", active: "border-green-600 bg-green-600 text-white" },
];

export function progressLabel(p: Progress): string {
  return PROGRESS_OPTIONS.find((o) => o.value === p)?.label ?? "Not started";
}

function useProgressText() {
  const { t } = useTranslation();
  return (p: Progress) => t(`objectives.progress.${p ?? "none"}`, progressLabel(p));
}

/** A small coloured pill showing where an objective stands. */
export function ProgressPill({ progress }: { progress: Progress }) {
  const text = useProgressText();
  const cls =
    progress === "done" ? "bg-green-50 text-green-700"
      : progress === "need_help" ? "bg-amber-50 text-amber-800"
        : progress === "on_track" ? "bg-blue-50 text-blue-700"
          : "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      {progress === "done" && <Check size={11} aria-hidden />}
      {progress === "need_help" && <CircleAlert size={11} aria-hidden />}
      {text(progress)}
    </span>
  );
}

/** "By 4 Feb 2027", amber when the date has passed and it isn't done. */
export function DueLine({ due, done }: { due: string | null; done: boolean }) {
  const { t } = useTranslation();
  if (!due) return null;
  const days = daysUntil(due);
  const late = !done && days != null && days < 0;
  return (
    <span className={late ? "font-semibold text-amber-800" : "text-gray-500"}>
      {late
        ? t("objectives.wasDue", "Was due {{date}}", { date: formatDate(due) })
        : t("objectives.dueBy", "By {{date}}", { date: formatDate(due) })}
    </span>
  );
}

/** Buttons to set where an objective stands. Tapping the current one clears it. */
export function ProgressPicker({ value, onChange, disabled }: { value: Progress; onChange: (p: Progress) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const text = useProgressText();
  return (
    <div role="group" aria-label={t("objectives.progressLabel", "Progress")} className="flex flex-wrap gap-1.5">
      {PROGRESS_OPTIONS.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onChange(on ? null : o.value)}
            className={`inline-flex min-h-[36px] items-center rounded-full border px-3 text-xs font-semibold disabled:opacity-60 ${
              on ? o.active : `border-gray-200 bg-white ${o.tone} hover:bg-gray-50`
            }`}
          >
            {text(o.value)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The box in the appraisal forms that says what happens to the objectives:
 * they become the person's goals for the year, shown on their Performance
 * page and gone through at every catch-up.
 */
export function ObjectivesHighlight({ name, own = false }: { name?: string; own?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="mb-3 flex gap-3 rounded-xl border-2 border-pme-blue/30 bg-pme-blue/5 p-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pme-blue text-white">
        <Target size={16} strokeWidth={2} aria-hidden />
      </span>
      <div className="text-xs text-gray-700">
        <p className="text-sm font-semibold text-pme-navy">
          {own
            ? t("objectives.highlightOwnTitle", "These become your objectives for the year")
            : t("objectives.highlightTitle", "These become {{name}}'s objectives for the year", { name: name || "the person" })}
        </p>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          <li>
            {own
              ? t("objectives.highlightOwn1", "Once agreed in the meeting, they sit at the top of your Performance page, each with its due date.")
              : t("objectives.highlight1", "Once agreed in the meeting, they sit at the top of {{name}}'s Performance page, each with its due date.", { name: name || "the person" })}
          </li>
          <li>
            {own
              ? t("objectives.highlightOwn2", "You and your manager go through them at every catch-up: on track, need help or done.")
              : t("objectives.highlight2", "You go through them at every catch-up and mark each one on track, need help or done.")}
          </li>
          <li>{t("objectives.highlight3", "They're the starting point for next year's appraisal, so make each one clear and checkable.")}</li>
        </ul>
      </div>
    </div>
  );
}

interface MyObjectivesData {
  review: {
    id: number;
    appraisal_year: number | null;
    manager_name: string | null;
    meeting_date: string | null;
    next_check_in: string | null;
    check_in_label: string | null;
  } | null;
  objectives: AgreedObjective[];
  development_plan: { support_needed: string; provided_by: string | null; target_date: string | null }[];
}

/**
 * Performance → My appraisal: the objectives agreed in the person's newest
 * finished appraisal, at the top of the page, with where each one stands.
 * The employee updates their own progress here.
 */
export default function MyObjectivesCard() {
  const { t } = useTranslation();
  const [data, setData] = useState<MyObjectivesData | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [noteFor, setNoteFor] = useState<number | null>(null);
  const [noteText, setNoteText] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await api.get<MyObjectivesData>("/api/objectives/my"));
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async (o: AgreedObjective, progress: Progress, note: string | null | undefined) => {
    setSavingId(o.id);
    setError("");
    try {
      const updated = await api.put<AgreedObjective>(`/api/objectives/${o.id}/progress`, { progress, note: note ?? null });
      setData((prev) => prev ? { ...prev, objectives: prev.objectives.map((x) => (x.id === o.id ? updated : x)) } : prev);
      setNoteFor(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("objectives.saveFailed", "That didn't save. Please try again."));
    } finally {
      setSavingId(null);
    }
  };

  if (!data?.review || (data.objectives.length === 0 && data.development_plan.length === 0)) return null;
  const { review, objectives, development_plan: plan } = data;
  const done = objectives.filter((o) => o.progress === "done").length;

  return (
    <section className="card overflow-hidden" aria-labelledby="my-objectives-title">
      <div className="border-b border-pme-blue/10 bg-pme-blue/5 px-5 py-4">
        <p className="eyebrow">{appraisalName(review.appraisal_year)}</p>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
          <h2 id="my-objectives-title" className="flex items-center gap-2 text-lg font-semibold text-pme-navy">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pme-blue text-white">
              <Target size={16} strokeWidth={2} aria-hidden />
            </span>
            {t("objectives.myTitle", "Your objectives for the year")}
          </h2>
          {objectives.length > 0 && (
            <span className="text-xs font-semibold text-gray-600">
              {t("objectives.doneCount", "{{done}} of {{total}} done", { done, total: objectives.length })}
            </span>
          )}
        </div>
        {objectives.length > 0 && (
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white" aria-hidden>
            <div className="h-full rounded-full bg-green-600" style={{ width: `${Math.round((done / objectives.length) * 100)}%` }} />
          </div>
        )}
        <p className="mt-2 text-xs text-gray-600">
          {t("objectives.myIntro", "Agreed with {{manager}} in your appraisal. Keep them up to date: you'll go through them together at each catch-up.", { manager: review.manager_name || t("appraisal.yourManager", "your manager") })}
        </p>
      </div>

      {error && <p role="alert" className="px-5 pt-3 text-xs text-red-600">{error}</p>}

      <ol className="divide-y divide-gray-100">
        {objectives.map((o, i) => (
          <li key={o.id} className="px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className={`text-sm font-semibold ${o.progress === "done" ? "text-gray-500 line-through decoration-gray-300" : "text-gray-900"}`}>{o.objective}</p>
                  <ProgressPill progress={o.progress} />
                </div>
                <p className="mt-0.5 text-xs">
                  <DueLine due={o.due_date} done={o.progress === "done"} />
                  {o.how_measured && <span className="text-gray-500"> · {t("objectives.measured", "How you'll know")}: {o.how_measured}</span>}
                </p>
                {o.progress_note && noteFor !== o.id && (
                  <p className="mt-1 rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-600">
                    <span className="font-semibold">{t("objectives.latest", "Latest")}:</span> {o.progress_note}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <ProgressPicker value={o.progress} disabled={savingId === o.id} onChange={(p) => void save(o, p, o.progress_note)} />
                  {noteFor !== o.id && (
                    <button type="button" onClick={() => { setNoteFor(o.id); setNoteText(o.progress_note ?? ""); }}
                      className="min-h-[36px] px-1 text-xs font-medium text-pme-blue hover:underline">
                      {o.progress_note ? t("objectives.editNote", "Edit update") : t("objectives.addNote", "Add an update")}
                    </button>
                  )}
                </div>
                {noteFor === o.id && (
                  <div className="mt-2">
                    <label className="block text-xs">
                      <span className="sr-only">{t("objectives.addNote", "Add an update")}</span>
                      <textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} className="input w-full"
                        placeholder={t("objectives.notePlaceholder", "e.g. Half done. Waiting on the new scanner.")} />
                    </label>
                    <div className="mt-1.5 flex gap-2">
                      <button type="button" disabled={savingId === o.id} onClick={() => void save(o, o.progress, noteText)}
                        className="inline-flex min-h-[36px] items-center rounded-lg bg-pme-blue px-3 text-xs font-semibold text-white disabled:opacity-60">
                        {t("common.save", "Save")}
                      </button>
                      <button type="button" onClick={() => setNoteFor(null)} className="min-h-[36px] rounded-lg px-3 text-xs text-gray-600 hover:bg-gray-50">
                        {t("common.cancel", "Cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>

      {plan.length > 0 && (
        <div className="border-t border-gray-100 px-5 py-4">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <Sprout size={14} aria-hidden /> {t("objectives.planTitle", "How you'll be supported")}
          </h3>
          <ul className="mt-2 space-y-1.5 text-sm text-gray-800">
            {plan.map((p, i) => (
              <li key={i} className="flex flex-wrap gap-x-2">
                <span>{p.support_needed}</span>
                <span className="text-xs text-gray-500">
                  {[p.provided_by, p.target_date ? `by ${formatDate(p.target_date)}` : null].filter(Boolean).join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/60 px-5 py-3 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          {review.next_check_in ? (
            <>
              <CalendarClock size={14} aria-hidden />
              {t("objectives.nextCheckIn", "Next catch-up with {{manager}}: {{date}}", { manager: review.manager_name || t("appraisal.yourManager", "your manager"), date: formatDate(review.next_check_in) })}
              {review.check_in_label ? ` (${review.check_in_label})` : ""}
            </>
          ) : (
            <>
              <TrendingUp size={14} aria-hidden />
              {t("objectives.noCheckIn", "Talk to your manager if an objective needs to change.")}
            </>
          )}
        </span>
        <Link to={`/review?review=${review.id}`} className="font-semibold text-pme-blue hover:underline">
          {t("objectives.readAppraisal", "Read the full appraisal")}
        </Link>
      </div>
    </section>
  );
}
